import type {
  UnitDef,
  UnitState,
  TechState,
  Resources,
  LifetimeResources,
  ProductionRates,
} from './types';
import { getUnitsForTier, getTierDef } from './units';
import { getTechForTier, getTechDef } from './tech';

// ─── Cost Formulas ──────────────────────────────────────────────────────────────

/** Cost to buy the next single unit. */
export function nextUnitCost(def: UnitDef, owned: number, techState: TechState): number {
  const scaleReduction = getTechCostScaleReduction(def, techState);
  const effectiveScale = Math.max(1.01, def.costScale - scaleReduction);
  const rawCost = def.baseCost * Math.pow(effectiveScale, owned);
  return applyTechCostReductions(rawCost, def, techState);
}

/** Total cost to buy `qty` units starting from `owned`. */
export function bulkUnitCost(
  def: UnitDef,
  owned: number,
  qty: number,
  techState: TechState
): number {
  const scaleReduction = getTechCostScaleReduction(def, techState);
  const s = Math.max(1.01, def.costScale - scaleReduction);
  const raw = def.baseCost * Math.pow(s, owned) * ((Math.pow(s, qty) - 1) / (s - 1));
  const bulkEfficiency = getTechBulkEfficiency(techState);
  return applyTechCostReductions(raw * (1 - bulkEfficiency), def, techState);
}

/** Maximum units buyable with current resources. */
export function maxAffordable(
  def: UnitDef,
  owned: number,
  resources: number,
  techState: TechState
): number {
  const scaleReduction = getTechCostScaleReduction(def, techState);
  const s = Math.max(1.01, def.costScale - scaleReduction);
  const costReduction = getTechCostReductionFactor(techState);
  const allCostPow = getTechAllCostPow(techState);
  const effectiveRes = resources;
  // Invert geometric series: n = log(1 + res*(s-1) / (baseCost*s^owned)) / log(s)
  const adjustedBase = def.baseCost * Math.pow(1 - costReduction, allCostPow === 1 ? 0 : 1);
  const n = Math.floor(
    Math.log(1 + (effectiveRes * (s - 1)) / (adjustedBase * Math.pow(s, owned))) / Math.log(s)
  );
  return Math.max(0, n);
}

function applyTechCostReductions(
  rawCost: number,
  _def: UnitDef,
  techState: TechState
): number {
  const reduction = getTechCostReductionFactor(techState);
  const pow = getTechAllCostPow(techState);
  return Math.pow(rawCost * (1 - reduction), pow);
}

// ─── Tech Derived Values ────────────────────────────────────────────────────────

export function getTechCostReductionFactor(techState: TechState): number {
  let total = 0;
  for (const [techId, level] of Object.entries(techState)) {
    const def = getTechDef(techId);
    if (!def || level === 0) continue;
    if (def.effect.type === 'cost_reduction') {
      total += def.effect.value * level;
    }
  }
  return Math.min(0.5, total);
}

export function getTechCostScaleReduction(def: UnitDef, techState: TechState): number {
  let total = 0;
  for (const [techId, level] of Object.entries(techState)) {
    const td = getTechDef(techId);
    if (!td || level === 0) continue;
    if (td.effect.type === 'cost_scale_reduction') {
      total += td.effect.value * level;
    }
  }
  return total;
}

export function getTechAllCostPow(techState: TechState): number {
  let pow = 1;
  for (const [techId, level] of Object.entries(techState)) {
    const td = getTechDef(techId);
    if (!td || level === 0) continue;
    if (td.effect.type === 'all_cost_pow') {
      pow *= Math.pow(td.effect.value, level);
    }
  }
  return pow;
}

export function getTechBulkEfficiency(techState: TechState): number {
  let total = 0;
  for (const [techId, level] of Object.entries(techState)) {
    const td = getTechDef(techId);
    if (!td || level === 0) continue;
    if (td.effect.type === 'bulk_efficiency') {
      total += td.effect.value * level;
    }
  }
  return Math.min(0.5, total);
}

export function getTechPurchaseRefund(techState: TechState): number {
  let total = 0;
  for (const [techId, level] of Object.entries(techState)) {
    const td = getTechDef(techId);
    if (!td || level === 0) continue;
    if (td.effect.type === 'purchase_refund') {
      total += td.effect.value * level;
    }
  }
  return Math.min(0.3, total);
}

/** Max unit ID that autobuy covers (0 = none). */
export function getAutobuyMaxUnit(techState: TechState): number {
  let max = 0;
  let hasSmartAlloc = false;
  let hasFullAuto = false;
  for (const [techId, level] of Object.entries(techState)) {
    const td = getTechDef(techId);
    if (!td || level === 0) continue;
    if (td.effect.type === 'autobuy') max = Math.max(max, td.effect.value);
    if (td.effect.type === 'smart_allocation') hasSmartAlloc = true;
    if (td.effect.type === 'full_automation') hasFullAuto = true;
  }
  if (hasFullAuto) return 25;
  return max;
}

export function hasSmartAllocation(techState: TechState): boolean {
  for (const [techId, level] of Object.entries(techState)) {
    const td = getTechDef(techId);
    if (td && level > 0 && td.effect.type === 'smart_allocation') return true;
  }
  return false;
}

// ─── Softcap ────────────────────────────────────────────────────────────────────

export function softcap(x: number, threshold: number, power: number): number {
  if (x <= threshold) return x;
  return threshold * Math.pow(x / threshold, power);
}

// ─── Multiplier Computation ────────────────────────────────────────────────────

export interface MultiplierSet {
  productionChain: number; // from production amplifiers
  creation: number;        // from creation chain amplifiers
  knowledge: number;       // from knowledge amplifiers
  energy: number;          // from energy amplifiers
  warfareEcon: number;     // small bonus from warfare units 24/25
  techProd: number;        // additive from production tech
  techAttack: number;
  techDefense: number;
  techRaidGain: number;
  logScaling: number;      // from log-based techs (t_p5 style)
  prestige: number;
  ascension: number;
  event: number;
}

export function computeMultipliers(
  tier: number,
  units: UnitState,
  tech: TechState,
  lifetime: LifetimeResources,
  prestigeMult: number,
  ascensionMult: number,
  eventMult: number
): MultiplierSet {
  const defs = getUnitsForTier(tier);

  // Helpers to get count safely
  const owned = (id: number) => units[id] ?? 0;

  // ── Production chain amplifiers (linear) ───────────────────────────────────
  let productionChainMult = 1;
  for (const def of defs) {
    if (def.role !== 'amplifier') continue;
    if (def.ampTargets !== 'production') continue;
    const n = owned(def.id);
    if (n === 0) continue;
    if (def.ampFormula === 'linear' && def.bonusPerUnit) {
      productionChainMult *= (1 + n * def.bonusPerUnit);
    } else if (def.ampFormula === 'log' && def.logFactor) {
      const lifeVal = lifetime.primary;
      const logVal = Math.log10(1 + lifeVal);
      productionChainMult *= (1 + logVal * def.logFactor * n);
    }
  }
  productionChainMult = softcap(productionChainMult, 1000, 0.6);

  // ── Creation amplifiers (global, linear) ──────────────────────────────────
  let creationMult = 1;
  for (const def of defs) {
    if (def.role !== 'amplifier') continue;
    if (def.chain !== 'creation') continue;
    if (def.ampTargets !== 'global') continue;
    const n = owned(def.id);
    if (n === 0) continue;
    if (def.ampFormula === 'linear' && def.bonusPerUnit) {
      creationMult *= (1 + n * def.bonusPerUnit);
    }
  }

  // ── Energy amplifiers (global) ─────────────────────────────────────────────
  let energyMult = 1;
  for (const def of defs) {
    if (def.chain !== 'energy') continue;
    if (def.role !== 'amplifier') continue;
    const n = owned(def.id);
    if (n === 0) continue;
    if (def.ampFormula === 'exponential' && def.ampBase) {
      // Unit 22: Core Taps / Proto Dyson Rings — cap to prevent runaway
      const cappedN = Math.min(n, 100);
      energyMult *= softcap(Math.pow(def.ampBase, cappedN), 1e6, 0.5);
    }
  }

  // ── Knowledge amplifiers (global log) ─────────────────────────────────────
  let knowledgeMult = 1;
  for (const def of defs) {
    if (def.chain !== 'knowledge') continue;
    if (def.role !== 'amplifier') continue;
    const n = owned(def.id);
    if (n === 0) continue;
    if (def.ampFormula === 'log' && def.logFactor) {
      const lifeVal = lifetime.knowledge;
      const logVal = Math.log10(1 + lifeVal);
      knowledgeMult *= (1 + logVal * def.logFactor * n);
    }
  }
  knowledgeMult = softcap(knowledgeMult, 100, 0.65);

  // ── Global exponential (Unit 10 / System Megafactories) ───────────────────
  // Handled via productionChainMult since ampTargets === 'global' but role is amplifier
  // We fold it in here separately for clarity
  let globalExpMult = 1;
  for (const def of defs) {
    if (def.role !== 'amplifier') continue;
    if (def.ampTargets !== 'global') continue;
    if (def.ampFormula !== 'exponential') continue;
    const n = owned(def.id);
    if (n === 0) continue;
    const cappedN = Math.min(n, 50);
    globalExpMult *= softcap(Math.pow(def.ampBase!, cappedN), 1e8, 0.45);
  }

  // ── Warfare economy bonus (units 24/25 prodBonusPerUnit) ─────────────────
  let warfareEconMult = 1;
  for (const def of defs) {
    if (def.chain !== 'warfare') continue;
    const n = owned(def.id);
    if (n > 0 && def.prodBonusPerUnit) {
      warfareEconMult += n * def.prodBonusPerUnit;
    }
  }

  // ── Tech production multipliers ────────────────────────────────────────────
  let techProdMult = 1;
  let techAttackMult = 1;
  let techDefenseMult = 1;
  let techRaidGainMult = 1;
  let logScalingBonus = 0;

  const tierTechs = getTechForTier(tier);
  for (const td of tierTechs) {
    const level = tech[td.id] ?? 0;
    if (level === 0) continue;
    switch (td.effect.type) {
      case 'all_production_mult':
        techProdMult *= (1 + td.effect.value * level);
        break;
      case 'unit_output_mult':
        // Applied per-unit in production rate calc
        break;
      case 'chain_mult':
        // Applied per-unit in production rate calc
        break;
      case 'attack_mult':
        techAttackMult *= (1 + td.effect.value * level);
        break;
      case 'defense_mult':
        techDefenseMult *= (1 + td.effect.value * level);
        break;
      case 'raid_gain_mult':
        techRaidGainMult *= (1 + td.effect.value * level);
        break;
      case 'log_scaling':
        logScalingBonus += (1 + Math.log10(1 + lifetime.primary) * td.effect.value);
        break;
    }
  }

  return {
    productionChain: productionChainMult * globalExpMult,
    creation: creationMult,
    knowledge: knowledgeMult,
    energy: energyMult,
    warfareEcon: warfareEconMult,
    techProd: techProdMult * (1 + logScalingBonus),
    techAttack: techAttackMult,
    techDefense: techDefenseMult,
    techRaidGain: techRaidGainMult,
    logScaling: 1 + logScalingBonus,
    prestige: prestigeMult,
    ascension: ascensionMult,
    event: eventMult,
  };
}

// ─── Production Rate Computation ───────────────────────────────────────────────

export function computeProductionRates(
  tier: number,
  units: UnitState,
  tech: TechState,
  lifetime: LifetimeResources,
  prestigeMult: number,
  ascensionMult: number,
  eventMult: number
): ProductionRates {
  const defs = getUnitsForTier(tier);
  const mults = computeMultipliers(tier, units, tech, lifetime, prestigeMult, ascensionMult, eventMult);
  const owned = (id: number) => units[id] ?? 0;
  const tierTechs = getTechForTier(tier);

  const globalMult =
    mults.productionChain *
    mults.creation *
    mults.knowledge *
    mults.energy *
    mults.warfareEcon *
    mults.techProd *
    mults.prestige *
    mults.ascension *
    mults.event;

  // ── Primary (Matter/Alloys) ─────────────────────────────────────────────────
  // Effective unit 1 count = owned(1) + fractional from builders
  let effectiveUnit1 = owned(1);

  // Builder chain: each builder adds fractional copies of their target
  // We compute this bottom-up: deepest builders first
  const builderContrib: Record<number, number> = {};
  for (const def of defs) {
    if (def.role !== 'builder' || !def.builderTarget) continue;
    const contrib = owned(def.id) * (def.builderOutput ?? 0);
    builderContrib[def.builderTarget] = (builderContrib[def.builderTarget] ?? 0) + contrib;
  }

  // Propagate builder chain upward (one pass is enough for a linear chain)
  // builders contribute to their target's effective count
  for (let id = 1; id <= 25; id++) {
    const extra = builderContrib[id] ?? 0;
    if (extra > 0 && id === 1) effectiveUnit1 += extra;
  }

  // Per-unit tech multipliers
  const getUnitTechMult = (unitId: number): number => {
    let m = 1;
    for (const td of tierTechs) {
      const level = tech[td.id] ?? 0;
      if (level === 0) continue;
      if (td.effect.type === 'unit_output_mult' && td.effect.targetUnitId === unitId) {
        m *= (1 + td.effect.value * level);
      }
      if (td.effect.type === 'chain_mult' && td.effect.chainMaxUnit && unitId <= td.effect.chainMaxUnit) {
        m *= (1 + td.effect.value * level);
      }
    }
    return m;
  };

  const unit1Def = defs.find((d) => d.id === 1);
  const unit1OutputMult = getUnitTechMult(1);
  const primaryPerSec = effectiveUnit1 * (unit1Def?.baseOutput ?? 1) * unit1OutputMult * globalMult;

  // ── Knowledge ──────────────────────────────────────────────────────────────
  const knowledgeDef = defs.find((d) => d.id === 15);
  let effectiveUnit15 = owned(15);
  const u15contrib = builderContrib[15] ?? 0;
  effectiveUnit15 += u15contrib;
  const knowledgePerSec = effectiveUnit15 * (knowledgeDef?.baseOutput ?? 1) * globalMult;

  // ── Energy ─────────────────────────────────────────────────────────────────
  const energyDef = defs.find((d) => d.id === 19);
  let effectiveUnit19 = owned(19);
  const u19contrib = builderContrib[19] ?? 0;
  effectiveUnit19 += u19contrib;
  const energyPerSec = effectiveUnit19 * (energyDef?.baseOutput ?? 1) * globalMult;

  // ── PvP (Influence/Fleet Command) — no global mult, just base rate ────────
  const pvpDef = defs.find((d) => d.id === 23);
  const pvpPerSec = owned(23) * (pvpDef?.baseOutput ?? 1);

  return {
    primaryPerSec,
    knowledgePerSec,
    energyPerSec,
    pvpPerSec,
  };
}

// ─── PvP Formulas ───────────────────────────────────────────────────────────────

export function computeAttackPower(tier: number, units: UnitState, tech: TechState): number {
  const defs = getUnitsForTier(tier);
  const unit25 = defs.find((d) => d.id === 25);
  const n = units[25] ?? 0;
  const baseAttack = n * (unit25?.attackPerUnit ?? 12);
  const mults = computeMultipliers(tier, units, tech, { primary: 0, knowledge: 0, energy: 0, pvp: 0 }, 1, 1, 1);
  return Math.pow(baseAttack * mults.techAttack, 0.9); // compression
}

export function computeDefensePower(tier: number, units: UnitState, tech: TechState): number {
  const defs = getUnitsForTier(tier);
  const unit24 = defs.find((d) => d.id === 24);
  const n = units[24] ?? 0;
  const baseDef = n * (unit24?.defensePerUnit ?? 10);
  const mults = computeMultipliers(tier, units, tech, { primary: 0, knowledge: 0, energy: 0, pvp: 0 }, 1, 1, 1);
  return Math.pow(baseDef * mults.techDefense, 0.9);
}

export function computeRaidGain(
  attackerAttack: number,
  defenderDefense: number,
  defenderPrimaryPerSec: number,
  raidGainTechMult: number
): { gain: number; debuffAmount: number; debuffDuration: number } {
  const powerRatio = attackerAttack / Math.max(defenderDefense, 1);
  const effectiveRatio = Math.min(Math.max(powerRatio, 0.25), 4.0);
  const successScore = Math.pow(effectiveRatio, 0.75);

  const gain = defenderPrimaryPerSec * 10 * 0.10 * successScore * raidGainTechMult; // 10s of production
  const debuffAmount = Math.min(0.20, 0.05 * successScore);
  const debuffDuration = 300; // 5 minutes in seconds

  return { gain, debuffAmount, debuffDuration };
}

export function computeRaidSlots(units25Owned: number): number {
  return 1 + Math.floor(units25Owned / 5);
}

export function computeRaidCost(currentActiveRaids: number): number {
  return 100 * (1 + currentActiveRaids * 0.5);
}

// ─── Offline Progress ───────────────────────────────────────────────────────────

export function computeOfflineGains(
  rates: ProductionRates,
  offlineSeconds: number,
  offlineCapSeconds: number,
  offlineMult: number
): Resources {
  const effectiveTime = Math.min(offlineSeconds, offlineCapSeconds);
  return {
    primary: rates.primaryPerSec * effectiveTime * offlineMult,
    knowledge: rates.knowledgePerSec * effectiveTime * offlineMult,
    energy: rates.energyPerSec * effectiveTime * offlineMult,
    pvp: rates.pvpPerSec * effectiveTime * offlineMult,
  };
}

// ─── Ascension ──────────────────────────────────────────────────────────────────

export function computeAscensionPoints(lifetimePrimary: number, tier: number): number {
  const threshold = 1e12 * Math.pow(1000, tier - 1);
  return Math.floor(Math.pow(lifetimePrimary / threshold, 0.5));
}

export function computeAscensionMult(ascensionPoints: number): number {
  return 1 + Math.log10(1 + ascensionPoints) * 0.25;
}

// ─── Power Score (for matchmaking) ─────────────────────────────────────────────

export function computePowerScore(rates: ProductionRates, tech: TechState, ascensionMult: number): number {
  const techFactor = Object.values(tech).reduce((sum, lvl) => sum + lvl, 0) * 0.01;
  return Math.pow(rates.primaryPerSec, 0.5) * (1 + techFactor) * ascensionMult;
}

// ─── Tier Unlock Check ──────────────────────────────────────────────────────────

export function checkTierUnlock(
  targetTier: number,
  units: UnitState,
  lifetime: LifetimeResources
): boolean {
  // Import inline to avoid circular dep issues in bundler
  const { TIER_DEFS } = require('./units') as { TIER_DEFS: import('./types').TierDef[] };
  const tierDef = TIER_DEFS.find((t) => t.id === targetTier);
  if (!tierDef) return false;
  const req = tierDef.unlockRequirement;
  if (req.type === 'unit_owned') {
    return (units[req.unitId!] ?? 0) >= (req.amount ?? 1);
  }
  if (req.type === 'lifetime_resource') {
    const val = req.resourceKey === 'primary' ? lifetime.primary :
                req.resourceKey === 'knowledge' ? lifetime.knowledge :
                req.resourceKey === 'energy' ? lifetime.energy : lifetime.pvp;
    return val >= (req.amount ?? 1);
  }
  return false;
}

// ─── Tech Cost ──────────────────────────────────────────────────────────────────

export function techCost(def: { baseCost: number; costGrowth: number }, currentLevel: number): number {
  return def.baseCost * Math.pow(def.costGrowth, currentLevel);
}

// ─── Number Formatting ──────────────────────────────────────────────────────────

const SUFFIXES = ['', 'K', 'M', 'B', 'T', 'Qa', 'Qi', 'Sx', 'Sp', 'Oc', 'No', 'Dc'];

export function formatNumber(n: number): string {
  if (!isFinite(n) || isNaN(n)) return '0';
  if (n < 0) return '-' + formatNumber(-n);
  if (n < 1000) return n.toFixed(n < 10 ? 2 : 0);

  const exp = Math.floor(Math.log10(n) / 3);
  if (exp < SUFFIXES.length) {
    const val = n / Math.pow(1000, exp);
    return val.toFixed(val < 10 ? 2 : 1) + SUFFIXES[exp];
  }
  // Scientific notation for truly massive numbers
  const e = Math.floor(Math.log10(n));
  const mantissa = n / Math.pow(10, e);
  return mantissa.toFixed(2) + 'e' + e;
}

export function formatRate(perSec: number): string {
  return formatNumber(perSec) + '/s';
}
